import maplibregl from 'maplibre-gl';
import Supercluster from 'supercluster';

export class MapController {
    constructor(options = {}) {
        this.stationsData = options.stationsData || [];
        this.electionData = options.electionData || null;
        this.onStoryChange = options.onStoryChange || null;
        
        this.map = null;
        this.cluster = null;
        this.markers = new Map();
        this.currentPopup = null;
        this.showElectionLayer = true;
        this.showOnlyAtRisk = true; // Default to showing only at-risk stations
        this.filteredStationsData = [];
        
        this.init();
    }

    init() {
        this.initializeMap();
        this.filterStations(); // Apply initial filter
        this.setupControls();
        this.setupEventListeners();
    }

    initializeMap() {
        this.map = new maplibregl.Map({
            container: 'map',
            style: this.getMapStyle(),
            center: [-98.5795, 39.8283], // Center of US
            zoom: 4,
            minZoom: 3,
            maxZoom: 18,
            antialias: true
        });

        this.map.on('load', () => {
            this.onMapLoad();
        });

        this.map.on('zoom', () => {
            this.updateMarkers();
        });

        this.map.on('move', () => {
            this.updateMarkers();
        });
    }

    getMapStyle() {
        // Using a bright, Google Maps-like style
        return {
            version: 8,
            name: 'NPR Station Map',
            sources: {
                'osm': {
                    type: 'raster',
                    tiles: [
                        'https://a.tile.openstreetmap.org/{z}/{x}/{y}.png',
                        'https://b.tile.openstreetmap.org/{z}/{x}/{y}.png',
                        'https://c.tile.openstreetmap.org/{z}/{x}/{y}.png'
                    ],
                    tileSize: 256,
                    attribution: '© OpenStreetMap contributors'
                }
            },
            layers: [
                {
                    id: 'osm-tiles',
                    type: 'raster',
                    source: 'osm',
                    minzoom: 0,
                    maxzoom: 19
                }
            ]
        };
    }

    onMapLoad() {
        // Add election data source if available
        if (this.electionData) {
            this.addElectionLayer();
            this.createElectionLegend();
        }

        // Initialize markers
        this.updateMarkers();
        
        console.log('Map loaded successfully');
    }

    addElectionLayer() {
        // Add election results as a layer
        this.map.addSource('election-results', {
            type: 'geojson',
            data: this.electionData
        });

        this.map.addLayer({
            id: 'election-results-fill',
            type: 'fill',
            source: 'election-results',
            paint: {
                'fill-color': [
                    'interpolate',
                    ['linear'],
                    ['get', 'per_gop'],
                    0, '#3b82f6',
                    0.5, '#e5e7eb',
                    1, '#ef4444'
                ],
                'fill-opacity': 0.4
            },
            layout: {
                'visibility': 'visible'
            }
        });

        this.map.addLayer({
            id: 'election-results-stroke',
            type: 'line',
            source: 'election-results',
            paint: {
                'line-color': '#ffffff',
                'line-width': 0.5,
                'line-opacity': 0.8
            },
            layout: {
                'visibility': 'visible'
            }
        });
    }

    filterStations() {
        if (this.showOnlyAtRisk) {
            // Filter stations with >30% federal funding
            this.filteredStationsData = this.stationsData.filter(station => {
                const federalSupport = station.federalSupport; // Use camelCase field name
                if (!federalSupport || federalSupport === 'Unknown' || federalSupport === '') return false;
                
                // Parse percentage (remove % and convert to number)
                const percentage = parseFloat(federalSupport.replace('%', ''));
                return !isNaN(percentage) && percentage > 30;
            });
        } else {
            this.filteredStationsData = [...this.stationsData];
        }
        
        console.log(`Filter applied: ${this.showOnlyAtRisk ? 'Most at Risk' : 'All Stations'}`);
        console.log(`Showing ${this.filteredStationsData.length} out of ${this.stationsData.length} stations`);
        
        // Re-setup cluster with filtered data
        this.setupCluster();
        this.updateMarkers();
    }

    setupCluster() {
        const dataToUse = this.filteredStationsData.length > 0 ? this.filteredStationsData : this.stationsData;
        if (dataToUse.length === 0) return;

        this.cluster = new Supercluster({
            radius: 60,
            maxZoom: 12,
            minZoom: 0,
            minPoints: 2
        });

        // Prepare data for clustering
        const points = dataToUse.map(station => ({
            type: 'Feature',
            properties: { ...station },
            geometry: {
                type: 'Point',
                coordinates: [station.longitude, station.latitude]
            }
        }));

        this.cluster.load(points);
    }

    updateMarkers() {
        if (!this.map.isStyleLoaded() || !this.cluster) return;

        const zoom = this.map.getZoom();
        const bounds = this.map.getBounds();
        
        const bbox = [
            bounds.getWest(),
            bounds.getSouth(),
            bounds.getEast(),
            bounds.getNorth()
        ];

        const clusters = this.cluster.getClusters(bbox, Math.floor(zoom));

        // Clear existing markers
        this.markers.forEach(marker => marker.remove());
        this.markers.clear();

        clusters.forEach(cluster => {
            const [longitude, latitude] = cluster.geometry.coordinates;
            const { cluster: isCluster, point_count } = cluster.properties;

            const el = document.createElement('div');

            if (isCluster) {
                // Cluster marker
                el.className = 'cluster-marker';
                el.style.width = `${30 + (point_count / this.stationsData.length) * 20}px`;
                el.style.height = `${30 + (point_count / this.stationsData.length) * 20}px`;
                el.innerHTML = point_count;

                el.addEventListener('click', () => {
                    const expansionZoom = Math.min(
                        this.cluster.getClusterExpansionZoom(cluster.id),
                        18
                    );
                    this.map.easeTo({
                        center: [longitude, latitude],
                        zoom: expansionZoom,
                        duration: 500
                    });
                });
            } else {
                // Individual station marker
                el.className = 'station-marker';
                if (zoom > 8) el.classList.add('large');

                el.addEventListener('click', (e) => {
                    e.stopPropagation();
                    this.showStationPopup(cluster.properties, [longitude, latitude]);
                });
            }

            const marker = new maplibregl.Marker({ element: el })
                .setLngLat([longitude, latitude])
                .addTo(this.map);

            this.markers.set(cluster.id || cluster.properties.id, marker);
        });
    }

    showStationPopup(station, coordinates) {
        // Close existing popup
        if (this.currentPopup) {
            this.currentPopup.remove();
        }

        const popup = document.getElementById('station-popup');
        
        // Update popup content - use callSign field and create better name display
        const stationCall = station.callSign || station.call || 'Unknown';
        const stationName = station.name || station.callSign || station.call || 'NPR Station';
        
        // Create a more descriptive title
        let displayName = stationName;
        if (station.frequency) {
            displayName = `${stationCall} - ${station.frequency}`;
        }
        if (station.city && station.state) {
            displayName += ` • ${station.city}, ${station.state}`;
        }
        
        popup.querySelector('.station-call-letters').textContent = stationCall;
        popup.querySelector('.station-name').textContent = displayName;
        
        const phoneLink = popup.querySelector('.station-phone');
        if (station.phone) {
            phoneLink.href = `tel:${station.phone}`;
            phoneLink.textContent = station.phone;
            phoneLink.parentElement.style.display = 'flex';
        } else {
            phoneLink.parentElement.style.display = 'none';
        }
        
        const websiteLink = popup.querySelector('.station-website');
        const stationWebsite = station.website || (station.urls && station.urls.length > 0 ? station.urls[0].href : null);
        
        if (stationWebsite && stationWebsite.trim() !== '') {
            websiteLink.href = stationWebsite;
            websiteLink.textContent = 'Visit Website';
            websiteLink.parentElement.style.display = 'flex';
        } else {
            websiteLink.parentElement.style.display = 'none';
        }
        
        const donateLink = popup.querySelector('.station-donate');
        const donationUrl = station.donationUrl || station.website || (station.urls && station.urls.length > 0 ? station.urls[0].href : null);
        
        if (donationUrl && donationUrl.trim() !== '') {
            donateLink.href = donationUrl;
            donateLink.textContent = 'Make a Donation';
            donateLink.parentElement.style.display = 'flex';
        } else {
            donateLink.href = 'https://www.npr.org/support/';
            donateLink.textContent = 'Support NPR';
            donateLink.parentElement.style.display = 'flex';
        }
        
        // Update programming information
        const programmingFormat = popup.querySelector('.programming-format');
        const programmingAffiliations = popup.querySelector('.programming-affiliations');
        
        if (programmingFormat) {
            programmingFormat.textContent = station.programmingFormat || '';
        }
        
        if (programmingAffiliations) {
            programmingAffiliations.textContent = station.affiliations || '';
        }
        
        // Show/hide programming info section based on available data
        const programmingInfo = popup.querySelector('.programming-info');
        if (programmingInfo) {
            const hasFormatInfo = station.programmingFormat && station.programmingFormat.trim() !== '';
            const hasAffiliationInfo = station.affiliations && station.affiliations.trim() !== '';
            
            if (hasFormatInfo || hasAffiliationInfo) {
                programmingInfo.style.display = 'block';
            } else {
                programmingInfo.style.display = 'none';
            }
        }

        // Update federal funding information
        const federalSupport = popup.querySelector('.federal-support');
        
        if (federalSupport) {
            federalSupport.textContent = station.federalSupport || 'Unknown';
        }
        
        // Show/hide funding info section and update status
        const fundingInfo = popup.querySelector('.funding-info');
        const fundingStatus = popup.querySelector('.funding-status');
        const fundingStatusText = popup.querySelector('.funding-status-text');
        const statusIndicator = popup.querySelector('.status-indicator');
        
        const hasFundingData = station.federalSupport && station.federalSupport.trim() !== '';
        
        if (fundingInfo) {
            fundingInfo.style.display = 'block'; // Always show the funding section
        }
        
        if (fundingStatus && fundingStatusText && statusIndicator) {
            if (hasFundingData) {
                const fundingPercent = parseFloat(station.federalSupport.replace('%', ''));
                
                if (!isNaN(fundingPercent)) {
                    if (fundingPercent >= 50) {
                        statusIndicator.className = 'status-indicator high-risk';
                        fundingStatusText.textContent = `High federal dependency (${station.federalSupport}) - Critical support needed`;
                    } else if (fundingPercent >= 25) {
                        statusIndicator.className = 'status-indicator medium-risk';
                        fundingStatusText.textContent = `Moderate federal dependency (${station.federalSupport}) - Your support appreciated`;
                    } else if (fundingPercent > 0) {
                        statusIndicator.className = 'status-indicator low-risk';
                        fundingStatusText.textContent = `Lower federal dependency (${station.federalSupport}) - Support helps maintain independence`;
                    } else {
                        statusIndicator.className = 'status-indicator no-data';
                        fundingStatusText.textContent = 'Federal funding percentage unknown - Support public radio';
                    }
                } else {
                    statusIndicator.className = 'status-indicator no-data';
                    fundingStatusText.textContent = 'Federal funding percentage unknown - Support public radio';
                }
            } else {
                statusIndicator.className = 'status-indicator no-data';
                fundingStatusText.textContent = 'Federal funding percentage unknown - Support public radio';
            }
        }

        // Position popup
        const point = this.map.project(coordinates);
        popup.style.left = `${point.x - 160}px`;
        popup.style.top = `${point.y - 200}px`;
        
        // Show popup
        popup.classList.remove('hidden');
        
        this.currentPopup = {
            remove: () => popup.classList.add('hidden')
        };
    }

    setupControls() {
        // Layer toggle button
        const layerToggle = document.getElementById('layer-toggle');
        layerToggle.addEventListener('click', () => {
            this.toggleElectionLayer();
        });
        
        // Set initial state of toggle button
        layerToggle.style.background = this.showElectionLayer ? '#FF6B35' : 'rgba(255, 255, 255, 0.95)';
        layerToggle.style.color = this.showElectionLayer ? 'white' : '#4a5568';

        // At-risk filter toggle
        const atRiskFilter = document.getElementById('at-risk-filter');
        atRiskFilter.addEventListener('change', (e) => {
            this.showOnlyAtRisk = e.target.checked;
            this.filterStations();
        });

        // Popup close button
        const popupClose = document.querySelector('.popup-close');
        popupClose.addEventListener('click', () => {
            if (this.currentPopup) {
                this.currentPopup.remove();
            }
        });

        // Close popup when clicking on map
        this.map.on('click', () => {
            if (this.currentPopup) {
                this.currentPopup.remove();
            }
        });
    }

    toggleElectionLayer() {
        if (!this.map.getLayer('election-results-fill')) return;

        this.showElectionLayer = !this.showElectionLayer;
        const visibility = this.showElectionLayer ? 'visible' : 'none';
        
        this.map.setLayoutProperty('election-results-fill', 'visibility', visibility);
        this.map.setLayoutProperty('election-results-stroke', 'visibility', visibility);

        const layerToggle = document.getElementById('layer-toggle');
        layerToggle.style.background = this.showElectionLayer ? '#FF6B35' : 'rgba(255, 255, 255, 0.95)';
        layerToggle.style.color = this.showElectionLayer ? 'white' : '#4a5568';
    }

    setupEventListeners() {
        // Handle window resize
        window.addEventListener('mapResize', () => {
            this.map.resize();
        });

        // Handle story changes
        window.addEventListener('storyChange', (e) => {
            this.handleStoryChange(e.detail.panelIndex);
        });
    }

    handleStoryChange(panelIndex) {
        // Only handle when story is closed (panelIndex = -1)
        if (panelIndex === -1) {
            // Story closed, return to overview
            this.map.easeTo({
                center: [-98.5795, 39.8283],
                zoom: 4,
                duration: 1000
            });
        } else {
            // Story is open, show overview
            this.map.easeTo({
                center: [-98.5795, 39.8283],
                zoom: 4,
                duration: 1000
            });
        }
    }

    createElectionLegend() {
        // Create legend container
        const legend = document.createElement('div');
        legend.id = 'election-legend';
        legend.className = 'election-legend';
        
        // Legend title
        const title = document.createElement('div');
        title.className = 'legend-title';
        title.textContent = '2024 Election Results';
        legend.appendChild(title);
        
        // Legend items
        const legendItems = [
            { color: '#3b82f6', label: 'Democratic', value: '0%' },
            { color: '#8b9dc3', label: 'Swing', value: '25%' },
            { color: '#e5e7eb', label: 'Even', value: '50%' },
            { color: '#f5a3a3', label: 'Swing', value: '75%' },
            { color: '#ef4444', label: 'Republican', value: '100%' }
        ];
        
        legendItems.forEach(item => {
            const legendItem = document.createElement('div');
            legendItem.className = 'legend-item';
            
            const colorBox = document.createElement('div');
            colorBox.className = 'legend-color';
            colorBox.style.backgroundColor = item.color;
            
            const labelText = document.createElement('span');
            labelText.className = 'legend-label';
            labelText.textContent = `${item.label} (${item.value})`;
            
            legendItem.appendChild(colorBox);
            legendItem.appendChild(labelText);
            legend.appendChild(legendItem);
        });
        
        // Add to map
        document.body.appendChild(legend);
        
        return legend;
    }

    getMap() {
        return this.map;
    }

    getStationsData() {
        return this.stationsData;
    }
}
